import React, { useEffect, useState } from 'react';

interface SplashScreenProps {
  onReady: () => void;
}

const POLL_INTERVAL_MS = 1500;
const MIN_SPLASH_MS = 1800;   // 빠르게 로딩되어도 최소 이 시간만큼 표시
const TIMEOUT_MS = 30000;

const MESSAGES = [
  '보안 포털을 초기화하는 중입니다...',
  '서버에 연결하는 중입니다...',
  '잠시만 기다려 주세요...',
];

export default function SplashScreen({ onReady }: SplashScreenProps) {
  const [fading, setFading] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [msgIndex, setMsgIndex] = useState(0);
  const [dots, setDots] = useState('');

  // 메시지 순환
  useEffect(() => {
    const id = setInterval(() => {
      setMsgIndex(i => (i + 1) % MESSAGES.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  // 점 애니메이션
  useEffect(() => {
    const id = setInterval(() => {
      setDots(d => (d.length >= 3 ? '' : d + '.'));
    }, 400);
    return () => clearInterval(id);
  }, []);

  // Health check 폴링
  useEffect(() => {
    const startedAt = Date.now();
    let done = false;

    const finish = () => {
      if (done) return;
      done = true;
      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, MIN_SPLASH_MS - elapsed);
      setTimeout(() => {
        setFading(true);
        setTimeout(onReady, 600); // fade-out 후 전환
      }, wait);
    };

    const check = async () => {
      try {
        const res = await fetch('/api/health', { cache: 'no-store' });
        if (res.ok) finish();
      } catch {
        // 아직 cold start 중 — 계속 폴링
      }
      if (!done && Date.now() - startedAt >= TIMEOUT_MS) {
        setTimedOut(true);
      }
    };

    check(); // 즉시 첫 시도
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [onReady]);

  const handleRetry = () => {
    setTimedOut(false);
    window.location.reload();
  };

  // ─── S 경로 총 길이: 40+20+40+20+40 = 160
  const PATH_LENGTH = 160;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#0f172a',
        zIndex: 9999,
        opacity: fading ? 0 : 1,
        transition: 'opacity 0.6s ease',
        gap: '32px',
      }}
    >
      {/* ── 로고 SVG 애니메이션 ── */}
      <svg
        viewBox="0 0 56 56"
        width="120"
        height="120"
        fill="none"
        aria-label="SecuCenter"
      >
        <style>{`
          /* S 경로: 선이 그려지는 애니메이션 */
          .s-path {
            stroke-dasharray: ${PATH_LENGTH};
            stroke-dashoffset: ${PATH_LENGTH};
            animation: draw-path 2.2s cubic-bezier(0.4, 0, 0.2, 1) 0.3s forwards;
          }
          @keyframes draw-path {
            to { stroke-dashoffset: 0; }
          }

          /* 노드 6개: 순서대로 scale 0→1 */
          .node { transform-box: fill-box; transform-origin: center; }
          .n1 { animation: pop-in 0.4s cubic-bezier(0.34,1.56,0.64,1) 0s    both; }
          .n2 { animation: pop-in 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.25s  both; }
          .n3 { animation: pop-in 0.4s cubic-bezier(0.34,1.56,0.64,1) 0.6s  both; }
          .n4 { animation: pop-in 0.4s cubic-bezier(0.34,1.56,0.64,1) 1.0s  both; }
          .n5 { animation: pop-in 0.4s cubic-bezier(0.34,1.56,0.64,1) 1.5s   both; }
          .n6 { animation: pop-in 0.4s cubic-bezier(0.34,1.56,0.64,1) 1.9s   both; }
          @keyframes pop-in {
            from { transform: scale(0); opacity: 0; }
            to   { transform: scale(1); opacity: 1; }
          }

          /* 완성 후 전체 로고 glow pulse */
          .logo-group {
            animation: glow-pulse 2s ease-in-out 2.5s infinite;
          }
          @keyframes glow-pulse {
            0%, 100% { filter: drop-shadow(0 0 4px rgba(59,130,246,0.4)); }
            50%       { filter: drop-shadow(0 0 12px rgba(59,130,246,0.8)); }
          }
        `}</style>

        <g className="logo-group">
          {/* S 경로 */}
          <path
            className="s-path"
            d="M48 8 L8 8 L8 28 L48 28 L48 48 L8 48"
            stroke="#e2e8f0"
            strokeWidth="5.5"
            strokeLinecap="butt"
            strokeLinejoin="miter"
          />

          {/* 노드 6개 — 경로 순서대로 */}
          <circle className="node n1" cx="48" cy="8"  r="4.5" fill="#3b82f6" />
          <circle className="node n2" cx="8"  cy="8"  r="4.5" fill="#3b82f6" />
          <circle className="node n3" cx="8"  cy="28" r="4.5" fill="#3b82f6" />
          <circle className="node n4" cx="48" cy="28" r="4.5" fill="#3b82f6" />
          <circle className="node n5" cx="48" cy="48" r="4.5" fill="#3b82f6" />
          <circle className="node n6" cx="8"  cy="48" r="4.5" fill="#3b82f6" />
        </g>
      </svg>

      {/* ── 로고 텍스트 ── */}
      <div
        style={{
          fontFamily: "'Segoe UI', system-ui, sans-serif",
          fontSize: '22px',
          fontWeight: 700,
          letterSpacing: '0.05em',
          animation: 'pop-in 0.4s ease 2.0s both',
          display: 'flex',
          gap: '2px',
        }}
      >
        <span style={{ color: '#e2e8f0' }}>Secu</span>
        <span style={{ color: '#3b82f6' }}>Center</span>
      </div>

      {/* ── 상태 메시지 or 타임아웃 ── */}
      {timedOut ? (
        <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px' }}>
          <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>
            서버 연결에 실패했습니다.
          </p>
          <button
            onClick={handleRetry}
            style={{
              padding: '8px 24px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            다시 시도
          </button>
        </div>
      ) : (
        <p
          style={{
            color: '#64748b',
            fontSize: '13px',
            margin: 0,
            minWidth: '220px',
            textAlign: 'center',
            animation: 'pop-in 0.4s ease 2.3s both',
          }}
        >
          {MESSAGES[msgIndex]}{dots}
        </p>
      )}
    </div>
  );
}
